import { split } from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
import { Kind, OperationTypeNode } from "graphql";

/**
 * Analytics query names that should be routed to the analytics backend.
 * These are heavy analytics workloads that are separated from critical application operations.
 *
 * Following a recent incident where heavy analytics queries caused OOM failures,
 * these queries are isolated to a dedicated analytics deployment to ensure that
 * heavy analytics queries cannot starve resources from or crash the main application backend.
 *
 * @see Analytics Operations to Isolate:
 * - proposalAnalytic: Aggregates proposal cell data across multiple dimensions
 * - dashboardChartData: Executes custom SQL queries for flexible dashboard charts
 * - auditEvents: ElasticSearch date histogram aggregations across audit log indices
 * - usersActivity: ElasticSearch multi-level aggregations for per-user activity metrics
 * - allProposalsIds: Returns all proposal IDs for the client (grows unbounded)
 */
export const ANALYTICS_QUERY_NAMES = new Set([
  "proposalAnalytic",
  "dashboardChartData",
  "auditEvents",
  "usersActivity",
  "allProposalsIds",
]);

/**
 * Determines if an operation should be routed to the analytics backend.
 * Only queries (not mutations) with specific root field names are routed to analytics.
 *
 * @param operation - The GraphQL operation from Apollo Client
 * @returns true if the operation should be routed to the analytics backend
 */
export function isAnalyticsOperation(
  operation: Parameters<Parameters<typeof split>[0]>[0],
): boolean {
  const definition = getMainDefinition(operation.query);

  // Only route queries, never mutations
  if (
    definition.kind !== Kind.OPERATION_DEFINITION ||
    definition.operation !== OperationTypeNode.QUERY
  ) {
    return false;
  }

  // Check if any of the root selection fields match analytics query names
  return definition.selectionSet.selections.some((selection) => {
    if (selection.kind === Kind.FIELD) {
      return ANALYTICS_QUERY_NAMES.has(selection.name.value);
    }
    return false;
  });
}
